import Vue, { VNode } from 'vue';

interface ResizeBindingValue {
  handler: (entry: ResizeObserverEntry | HTMLElement) => void;
  quiet?: boolean;
}

type Ctx = {
  observer?: ResizeObserver;
  onResize?: () => void;
  handler: (entry: ResizeObserverEntry | HTMLElement) => void;
  options: ResizeBindingValue;
};

function addResizeListener(el: HTMLElement, handler: (entry: any) => void, options: ResizeBindingValue) {
  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      if (!options.quiet) {
        for (const entry of entries) {
          handler(entry);
        }
      }
    });
    observer.observe(el);

    (el as any).__vResize__ = { observer, handler, options } as Ctx;
  } else {
    const onResize = () => {
      if (!options.quiet) {
        handler(el);
      }
    };
    window.addEventListener('resize', onResize, { passive: true });
    (el as any).__vResize__ = { onResize, handler, options } as Ctx;
  }
}

function removeResizeListener(el: HTMLElement) {
  const ctx = (el as any).__vResize__ as Ctx | undefined;
  if (!ctx) return;

  if (ctx.observer) {
    ctx.observer.disconnect();
  } else if (ctx.onResize) {
    window.removeEventListener('resize', ctx.onResize);
  }
  delete (el as any).__vResize__;
}

export const Resize = Vue.directive('resize', {
  inserted(el: HTMLElement, binding, vnode: VNode) {
    const value = binding.value as ResizeBindingValue | ((entry: any) => void);

    const handler = typeof value === 'function'
      ? value
      : value?.handler;

    if (!handler) {
      return;
    }

    const options: ResizeBindingValue = typeof value === 'object'
      ? value
      : { handler };

    addResizeListener(el, handler, options);

    if (!options.quiet) {
      handler(el);
    }
  },

  componentUpdated(el: HTMLElement, binding, vnode: VNode) {
    const ctx = (el as any).__vResize__ as Ctx | undefined;
    if (!ctx) return;

    const value = binding.value as ResizeBindingValue | ((entry: any) => void);

    const newHandler = typeof value === 'function'
      ? value
      : value?.handler;

    const newOptions: ResizeBindingValue = typeof value === 'object'
      ? value
      : { handler: newHandler! };

    if (ctx.handler !== newHandler) {
      removeResizeListener(el);
      addResizeListener(el, newHandler!, newOptions);
    }
  },

  unbind(el: HTMLElement) {
    removeResizeListener(el);
  },
});
